import { useCallback, useContext, useEffect, useState } from 'react';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useContent } from 'src/contexts/ContentContext';
import { ModalContext } from 'src/contexts/ModalContext';
import { PosFormField } from 'src/core/POS/PosFormField';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { PosTextArea } from 'src/core/POS/PosTextArea';
import { PosTextField } from 'src/core/POS/PosTextField';
import { Stack } from 'src/core/ui';
import {
  useCancelRetailSale,
  useGetRetailSaleDetails,
  useGetRetailSaleLineItems,
  useRefundRetailSale,
} from 'src/hooks/api/useRetailSaleDetails';
import { SectionContent } from 'src/modals/common/Detail/Detail.styled';
import { DetailSection } from 'src/modals/common/DetailSection';
import { ContentId } from 'src/utils/constants/contentId';
import { isSuccess } from 'src/utils/errorUtils';
import {
  ApiException,
  PointOfSalePaymentState,
  SaleCancellationReason,
  SaleLineItem,
} from 'src/WebApiController';

import * as styles from './RetailSaleDetailView.css';

// Local cancel reason labels — the enum values from WebApiController don't map
// 1:1 to the Figma labels, so we define a display map here.
const CANCEL_REASON_OPTIONS: Record<SaleCancellationReason, string> = {
  [SaleCancellationReason.EventCancellation]: 'Event Cancelled',
  [SaleCancellationReason.SellerFault]: 'Placed by mistake',
  [SaleCancellationReason.MarketplaceFault]: 'Payment dispute',
  [SaleCancellationReason.NoFault]: 'Customer requested change',
  [SaleCancellationReason.Other]: 'Others',
};

export type RetailSaleDetailActions = {
  handleCancelInvoice: () => Promise<void>;
  handleRefund: () => Promise<void>;
  handleCancelAndRefund: () => Promise<void>;
  isActioning: boolean;
};

type RetailSaleDetailViewProps = {
  saleId: number | null;
  onActionsReady?: (actions: RetailSaleDetailActions) => void;
};

export const RetailSaleDetailView = ({
  saleId,
  onActionsReady,
}: RetailSaleDetailViewProps) => {
  const { genericError, showErrorDialog } = useClientLogContext();
  const { closeModal } = useContext(ModalContext);

  const loadingMsg = useContent(ContentId.LoadingInvoiceDetails);
  const couldNotLoadMsg = useContent(ContentId.CouldNotLoadSaleDetails);
  const noSaleMsg = useContent(ContentId.NoSaleSelected);
  const summaryMsg = useContent(ContentId.Summary);
  const saleIdMsg = useContent(ContentId.SaleId);
  const listingIdMsg = useContent(ContentId.ListingId);
  const originalIdMsg = useContent(ContentId.OriginalId);
  const saleDateMsg = useContent(ContentId.SaleDate);
  const totalCostMsg = useContent(ContentId.TotalCost);
  const paymentStatusMsg = useContent(ContentId.PaymentStatus);
  const earningsMsg = useContent(ContentId.Earnings);
  const cancelReasonMsg = useContent(ContentId.CancelReason);
  const notesMsg = useContent(ContentId.Notes);
  const cancellationNotesMsg = useContent(ContentId.CancellationNotes);
  const refundMsg = useContent(ContentId.Refund);
  const refundToOriginalMsg = useContent(
    ContentId.RefundToOriginalPaymentMethod
  );
  const originalPaymentMsg = useContent(ContentId.OriginalPaymentMethodOnFile);
  const refundAmountMsg = useContent(ContentId.RefundAmount);
  const overviewMsg = useContent(ContentId.Overview);
  const ratePercentMsg = useContent(ContentId.RatePercent);
  const amountMsg = useContent(ContentId.Amount);
  const referenceMsg = useContent(ContentId.Reference);
  const totalMsg = useContent(ContentId.Total);
  const paidMsg = useContent(ContentId.Paid);
  const outstandingMsg = useContent(ContentId.Outstanding);
  const cancelledMsg = useContent(ContentId.Cancelled);

  const { data: sale, isLoading: saleLoading } =
    useGetRetailSaleDetails(saleId);
  const { data: lineItems, isLoading: lineItemsLoading } =
    useGetRetailSaleLineItems(saleId);

  const cancelMutation = useCancelRetailSale();
  const refundMutation = useRefundRetailSale();

  const [cancelReason, setCancelReason] =
    useState<SaleCancellationReason | null>(null);
  const [cancelNotes, setCancelNotes] = useState('');
  const [refundAmount, setRefundAmount] = useState('');
  const [isActioning, setIsActioning] = useState(false);

  const isLoading = saleLoading || lineItemsLoading;

  const handleCancelInvoice = useCallback(async () => {
    if (!saleId) return;
    setIsActioning(true);
    try {
      const result = await cancelMutation.mutateAsync({
        saleId,
        cancellationReason: cancelReason,
      });
      if (isSuccess(result)) {
        closeModal(true);
      } else {
        showErrorDialog(
          'SaleClient.rejectSale',
          {
            message: result.message ?? genericError,
            status: result.status!,
          } as ApiException,
          { trackErrorData: { saleId, cancelReason } }
        );
      }
    } finally {
      setIsActioning(false);
    }
  }, [
    cancelMutation,
    cancelReason,
    closeModal,
    genericError,
    saleId,
    showErrorDialog,
  ]);

  const handleRefund = useCallback(async () => {
    if (!saleId || !sale) return;
    setIsActioning(true);
    try {
      await refundMutation.mutateAsync({
        saleId,
        merchantReference: `sale-${saleId}`,
      });
      closeModal(true);
    } finally {
      setIsActioning(false);
    }
  }, [closeModal, refundMutation, sale, saleId]);

  const handleCancelAndRefund = useCallback(async () => {
    if (!saleId || !sale) return;
    setIsActioning(true);
    try {
      const cancelResult = await cancelMutation.mutateAsync({
        saleId,
        cancellationReason: cancelReason,
      });
      if (!isSuccess(cancelResult)) {
        showErrorDialog(
          'SaleClient.rejectSale',
          {
            message: cancelResult.message ?? genericError,
            status: cancelResult.status!,
          } as ApiException,
          { trackErrorData: { saleId, cancelReason } }
        );
        return;
      }
      await refundMutation.mutateAsync({
        saleId,
        merchantReference: `sale-${saleId}`,
      });
      closeModal(true);
    } finally {
      setIsActioning(false);
    }
  }, [
    cancelMutation,
    cancelReason,
    closeModal,
    genericError,
    refundMutation,
    sale,
    saleId,
    showErrorDialog,
  ]);

  useEffect(() => {
    onActionsReady?.({
      handleCancelInvoice,
      handleRefund,
      handleCancelAndRefund,
      isActioning,
    });
  }, [
    handleCancelInvoice,
    handleRefund,
    handleCancelAndRefund,
    isActioning,
    onActionsReady,
  ]);

  if (isLoading) {
    return <div className={styles.loadingContainer}>{loadingMsg}</div>;
  }

  if (!sale) {
    return (
      <div className={styles.loadingContainer}>
        {saleId ? couldNotLoadMsg : noSaleMsg}
      </div>
    );
  }

  const invoiceNumber = sale.idOnMkp ?? `${sale.id}`;
  const ticketCount = sale.ticketCnt ?? 0;
  const totalAmount = sale.totalPmt;
  const paymentState = sale.pmtState;
  const listingId = sale.listingId;
  const createdBy = sale.insertBy ?? undefined;
  const saleDate = sale.saleDate
    ? new Date(sale.saleDate).toLocaleDateString()
    : '-';

  const isPaid = paymentState === PointOfSalePaymentState.Paid;
  const isCancelledOrReversed =
    paymentState === PointOfSalePaymentState.Reversed ||
    paymentState === PointOfSalePaymentState.PendingReversal;

  const totalLineItemsAmount =
    lineItems?.reduce((sum, item) => sum + (item.cost ?? 0), 0) ?? 0;

  return (
    <Stack direction="column" gap="l">
      {/* INVOICE HEADER */}
      <div className={styles.headerSection}>
        <Stack direction="row" gap="m" alignItems="center">
          <span className={styles.invoiceNumber}>Invoice #{invoiceNumber}</span>
          <StatusBadge
            paymentState={paymentState}
            isCancelled={isCancelledOrReversed}
            paidLabel={paidMsg}
            outstandingLabel={outstandingMsg}
            cancelledLabel={cancelledMsg}
          />
        </Stack>
        <div className={styles.headerMeta}>
          {ticketCount} ticket{ticketCount !== 1 ? 's' : ''}
          {totalAmount ? ` · ${totalAmount.disp ?? totalAmount.amt}` : ''}
          {createdBy ? ` · Created by ${createdBy}` : ''}
        </div>
      </div>

      {/* SUMMARY section */}
      <DetailSection name={summaryMsg}>
        <SectionContent numOfColumns={1}>
          <div className={styles.summaryGrid}>
            <SummaryField label={saleIdMsg} value={`${sale.id}`} />
            <SummaryField
              label={listingIdMsg}
              value={listingId ? `${listingId}` : '-'}
            />
            <SummaryField
              label={originalIdMsg}
              value={
                sale.originatedFromListingId
                  ? `${sale.originatedFromListingId}`
                  : '-'
              }
            />
            <SummaryField label={saleDateMsg} value={saleDate} />
            <SummaryField
              label={totalCostMsg}
              value={
                totalAmount?.disp ?? (totalAmount ? `${totalAmount.amt}` : '-')
              }
            />
            <SummaryField
              label={paymentStatusMsg}
              value={paymentState ?? '-'}
            />
          </div>
        </SectionContent>
      </DetailSection>

      {/* EARNINGS section */}
      <DetailSection name={earningsMsg}>
        <SectionContent numOfColumns={1}>
          <EarningsTable
            lineItems={lineItems ?? []}
            totalAmount={totalLineItemsAmount}
            currencyCode={sale.currency}
            overviewLabel={overviewMsg}
            ratePercentLabel={ratePercentMsg}
            amountLabel={amountMsg}
            referenceLabel={referenceMsg}
            totalLabel={totalMsg}
            refundLabel={refundMsg}
          />
        </SectionContent>
      </DetailSection>

      {/* CANCELLATION section */}
      <DetailSection name={cancelReasonMsg}>
        <SectionContent numOfColumns={1}>
          <div className={styles.cancelReasonContainer}>
            <PosFormField label={cancelReasonMsg}>
              <PosEnumSelect<SaleCancellationReason>
                style={{ width: '100%' }}
                value={cancelReason}
                valueOptionsContent={CANCEL_REASON_OPTIONS}
                onChange={(value) => setCancelReason(value)}
              />
            </PosFormField>
            {/* TODO: rejectSale API does not accept notes yet — wire this up when backend adds support */}
            <PosFormField label={notesMsg}>
              <PosTextArea
                className={styles.notesTextArea}
                placeholder={cancellationNotesMsg}
                value={cancelNotes}
                onChange={(e) => setCancelNotes(e.target.value)}
              />
            </PosFormField>
          </div>
        </SectionContent>
      </DetailSection>

      {/* REFUND section */}
      <DetailSection name={refundMsg}>
        <SectionContent numOfColumns={1}>
          <Stack direction="column" gap="m">
            <Stack direction="row" gap="m" alignItems="center">
              <input
                type="radio"
                id="refundOriginalMethod"
                name="refundMethod"
                defaultChecked
                readOnly
              />
              <label htmlFor="refundOriginalMethod">
                {refundToOriginalMsg}
              </label>
            </Stack>
            {isPaid && (
              <div className={styles.paymentMethodDisplay}>
                {originalPaymentMsg}
              </div>
            )}
            <div className={styles.refundAmountContainer}>
              <PosFormField label={refundAmountMsg}>
                <PosTextField
                  type="number"
                  placeholder={totalAmount ? `${totalAmount.amt}` : '0.00'}
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                />
              </PosFormField>
            </div>
          </Stack>
        </SectionContent>
      </DetailSection>
    </Stack>
  );
};

// Sub-components

type StatusBadgeProps = {
  paymentState: PointOfSalePaymentState | null | undefined;
  isCancelled: boolean;
  paidLabel: string;
  outstandingLabel: string;
  cancelledLabel: string;
};

const StatusBadge = ({
  paymentState,
  isCancelled,
  paidLabel,
  outstandingLabel,
  cancelledLabel,
}: StatusBadgeProps) => {
  if (isCancelled) {
    return (
      <span className={styles.statusBadgeCancelled}>{cancelledLabel}</span>
    );
  }
  if (paymentState === PointOfSalePaymentState.Paid) {
    return <span className={styles.statusBadgePaid}>{paidLabel}</span>;
  }
  return <span className={styles.statusBadgeUnpaid}>{outstandingLabel}</span>;
};

type SummaryFieldProps = {
  label: string;
  value: string;
};

const SummaryField = ({ label, value }: SummaryFieldProps) => (
  <div>
    <div className={styles.summaryLabel}>{label}</div>
    <div className={styles.summaryValue}>{value}</div>
  </div>
);

type EarningsTableProps = {
  lineItems: SaleLineItem[];
  totalAmount: number;
  currencyCode: string;
  overviewLabel: string;
  ratePercentLabel: string;
  amountLabel: string;
  referenceLabel: string;
  totalLabel: string;
  refundLabel: string;
};

const EarningsTable = ({
  lineItems,
  totalAmount,
  currencyCode,
  overviewLabel,
  ratePercentLabel,
  amountLabel,
  referenceLabel,
  totalLabel,
  refundLabel,
}: EarningsTableProps) => {
  const chargeItems = lineItems.filter(
    (item) => item.cost != null && item.cost >= 0
  );
  const refundItems = lineItems.filter(
    (item) => item.cost != null && item.cost < 0
  );

  const formatAmount = (amount: number) =>
    `${currencyCode} ${Math.abs(amount).toFixed(2)}`;

  return (
    <div className={styles.earningsTableContainer}>
      <table className={styles.earningsTable}>
        <thead>
          <tr className={styles.tableHeaderRow}>
            <th className={styles.tableHeaderLeft}>{overviewLabel}</th>
            <th className={styles.tableHeaderRight}>{ratePercentLabel}</th>
            <th className={styles.tableHeaderRight}>{amountLabel}</th>
            <th className={styles.tableHeaderLeft}>{referenceLabel}</th>
          </tr>
        </thead>
        <tbody>
          {chargeItems.map((item) => (
            <tr key={item.id} className={styles.tableRow}>
              <td className={styles.tableCell}>
                {item.description ?? item.costType}
              </td>
              <td className={styles.tableCellRight}>-</td>
              <td className={styles.tableCellRight}>
                {formatAmount(item.cost)}
              </td>
              <td className={styles.tableCell}>
                {item.paymentReferenceId ?? '-'}
              </td>
            </tr>
          ))}
          {/* Total row */}
          <tr className={styles.tableTotalRow}>
            <td className={styles.tableCell}>{totalLabel}</td>
            <td className={styles.tableCellRight} />
            <td className={styles.tableCellRight}>
              {formatAmount(totalAmount)}
            </td>
            <td className={styles.tableCell} />
          </tr>
          {/* Payment history — refund entries */}
          {refundItems.map((item) => (
            <tr key={item.id} className={styles.paymentHistoryRow}>
              <td className={styles.tableCellMuted}>
                {item.description ?? refundLabel}
              </td>
              <td className={styles.tableCellRight} />
              <td className={styles.tableCellRight}>
                -{formatAmount(Math.abs(item.cost))}
              </td>
              <td className={styles.tableCellMuted}>
                {item.paymentReferenceId ?? '-'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
